<template>
    <div class="sign-documents">
        <b-col
            cols="12"
            lg="6"
            offset-lg="3"
        >
            <h1 class="text-center">Sign all documents</h1>

            <div class="my-3">
                <strong>{{ fullName }}</strong>, you are about to digitally sign the documents of
                your disclosure.
            </div>

            <div class="mb-4">
                Please be sure you've carefully reviewed each document in this disclosure. If you make any changes after
                signing, all parties will need to sign the revised documents.
            </div>

            <div class="mb-5">
                <b-form-row>
                    <b-col cols="12" sm="8">
                        <b-form-group class="mb-0">
                            <label
                                class="text-gray-dark font-size-4 font-weight-bold"
                                for="legal-name"
                            >
                                To sign, type your full legal name
                            </label>

                            <b-form-input
                                id="legal-name"
                                v-model="legalName"
                            ></b-form-input>

                            <b-form-invalid-feedback :state="legalNameValidationState">
                                {{ legalNameInvalidFeedback }}
                            </b-form-invalid-feedback>
                        </b-form-group>
                    </b-col>

                    <b-col cols="12" sm="4">
                        <b-form-group class="mb-0">
                            <label
                                class="text-gray-dark font-size-4 font-weight-bold"
                                for="date"
                            >
                                Date
                            </label>

                            <b-input-group>
                                <b-input-group-prepend>
                                    <span class="input-group-text-coherent">
                                        <i class="far fa-calendar-day font-size-3"/>
                                    </span>
                                </b-input-group-prepend>

                                <flat-pickr
                                    id="signature-date"
                                    v-model="signatureDate"
                                    :config="signatureDateConfig"
                                    class="form-control form-control-coherent"
                                />

                                <b-form-invalid-feedback :state="signatureDateValidationState">
                                    {{ signatureDateInvalidFeedback }}
                                </b-form-invalid-feedback>
                            </b-input-group>
                        </b-form-group>
                    </b-col>
                </b-form-row>
            </div>

            <div class="mb-5">
                <b-row>
                    <b-col cols="9" sm="10">
                        <div class="font-dancing-script font-size-2 border-bottom border-gray-500 mb-1">
                            <div ref="signature">
                                {{ legalName ? legalName : '&nbsp;' }}
                            </div>
                        </div>

                        <div class="text-gray-dark font-weight-bold">
                            Signature
                        </div>
                    </b-col>

                    <b-col cols="3" sm="2">
                        <div class="font-dancing-script font-size-2 border-bottom border-gray-500 mb-1">
                            {{ nameInitials }}
                        </div>

                        <div class="text-gray-dark font-weight-bold">
                            Initials
                        </div>
                    </b-col>
                </b-row>
            </div>

            <div class="font-size-6 text-gray-dark mb-3">
                By submitting this form, I agree that the signature as shown above will be the electronic representation
                of my signature for all purposes when used on this document - just the same as pen-and-paper signatures.
                Seller also acknowledges that the statements in this notice are true to the best of Seller's belief and
                that no person, including the broker(s), has instructed or influenced Seller to provide inaccurate
                information or to omit any material information.
            </div>

            <b-form-row class="justify-content-end my-3">
                <b-col>
                    <b-button
                        :disabled="saving"
                        block
                        size="lg"
                        variant="primary"
                        @click="sign"
                    >
                        <b-spinner
                            v-if="saving"
                            class="mr-1"
                            small
                        />

                        Sign
                    </b-button>
                </b-col>
            </b-form-row>
        </b-col>
    </div>
</template>

<script>
import SellerAppResource from "@sellers-shield-home-protection/ss-core-js/src/AppResources/SellerAppResource";
import DisclosureAppResource from "@sellers-shield-home-protection/ss-core-js/src/AppResources/DisclosureAppResource";
import {
    SECONDARY_DATE,
    SECONDARY_INITIALS,
    SECONDARY_NAME,
    SECONDARY_SIGNATURE
} from "@sellers-shield-home-protection/ss-core-js/src/AppResources/FormAppResource";
import {runAsPublic} from "@helpers/api-helper";
import FlatPickr from "vue-flatpickr-component";
import PusherMixin from '@mixins/PusherMixin';
import OrderAppResource from "@sellers-shield-home-protection/ss-core-js/src/AppResources/OrderAppResource";
import {usesDocumentGenerator} from '@helpers/order';
export default {
    name: "SignDocuments",
    components: {
        FlatPickr,
    },
    mixins: [
        PusherMixin,
    ],
    props: {
        order: {
            type: OrderAppResource,
            required: true
        },
        seller: {
            type: SellerAppResource,
            required: true
        },
        disclosure: {
            type: DisclosureAppResource,
            required: true
        },
    },

    data: () => ({
        signatureDateConfig: {
            wrap: true,
            altFormat: 'M j, Y',
            dateFormat: 'Y-m-d',
        },
        legalName: '',
        signatureDate: null,
        legalNameValidationState: null,
        signatureDateValidationState: null,
        legalNameInvalidFeedback: 'Please enter your full legal name.',
        signatureDateInvalidFeedback: 'Please choose a date.',
        saving: false,
        fileIsLoading: {},
    }),

    computed: {
        fullName() {
            return this.legalName ?
                this.legalName : this.seller.firstName + ' ' + this.seller.lastName;
        },
        nameInitials() {
            if (this.legalName) {
                return this.legalName.replace(/[^a-zA-Z- ]/g, "").match(/\b\w/g).join(' ');
            }
            return '';
        },
        isInvalid() {
            return [this.legalNameValidationState, this.signatureDateValidationState].some((state) => state === false)
        },
        formsAppResources() {
            return this.order?.forms?.toArray() || [];
        },
        orderAppResourceUuid() {
            return this.orderAppResource?._uuid || this.disclosure.orderUuid;
        },
    },

    methods: {
        validate() {
            this.legalNameValidationState = this.legalName ? null : false;
            this.signatureDateValidationState = this.signatureDate ? null : false;
        },

        async sign() {
            this.validate();

            if (this.isInvalid) {
                return;
            }

            try {
                this.saving = true;

                let data = {};
                data[SECONDARY_NAME] = this.legalName;
                data[SECONDARY_SIGNATURE] = this.legalName;
                data[SECONDARY_DATE] = this.signatureDate;
                data[SECONDARY_INITIALS] = this.nameInitials;

                //set all forms loading
                this.formsAppResources.forEach(form => this.fileIsLoading[form._uuid] = true);

                await runAsPublic(async () => {
                    try{
                        if(usesDocumentGenerator(this.order)){
                            await this.disclosure.signUsingDocumentGenerator(data);
                        }else{
                            await this.disclosure.sign(data);
                        }
                    }catch(e){
                        
                    }
                    this.emitEvents(10);
                });
            } catch (e) {
                console.error(e);
                this.showNotification({
                    message: 'Error signing forms. Please try again later',
                    type: 'error'
                });

                this.emitEvents(0.1);
            }
        },
        handleFormFileGenerated(data) {
            if (Boolean(data?.formUuid)) {
                delete this.fileIsLoading[data.formUuid];

                //if all files were generated we can reset page's state
                Object.values(this.fileIsLoading).length === 0 && this.emitEvents(0.1);
            }
        },
        emitEvents(timeout = 90) {
            setTimeout(async () => {
                //fallback refresh for pusher channel connection error
                this.$emit('fetch-data');
                this.saving = false;
                this.$emit('sign-documents');
            }, timeout * 1000);
        }
    },
    async mounted() {
        Boolean(this.orderAppResourceUuid) && this.subscribePusherChannel(
            `signature.${this.orderAppResourceUuid}`,
            {
                'filesGenerated': this.handleFormFileGenerated,
                'filesGenerateError': this.handleFormFileGenerated,
            }
        );
    },
    beforeDestroy() {
        Boolean(this.orderAppResourceUuid) && this.unsubscribePusherChannel(`signature.${this.orderAppResourceUuid}`);
    }
}
</script>